import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronUp } from 'react-icons/fi';
import useOpenLaborEpisodes from '../hooks/useOpenLaborEpisodes';
import { useOpenLaborChartFocus } from '../utils/openLaborChartFocus';
import { buildOpenLaborChartLocation, readLaborChartRoute, routeScopedLaborItems, shouldHideBackToLaborButton } from '../utils/backToLaborNavigation';

function LaborStatusBadge({ label, tone = 'neutral' }) {
  const toneClass = tone === 'accent'
    ? 'bg-amber-100 text-amber-900'
    : 'bg-sky-100 text-sky-900';
  return (
    <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${toneClass}`}>
      {label}
    </span>
  );
}

export default function BackToLaborButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const menuId = useId();
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const chartFocus = useOpenLaborChartFocus();

  const {
    data,
    isLoading,
    isFetching,
    isError,
  } = useOpenLaborEpisodes();

  const route = readLaborChartRoute(location);
  const hasRouteTarget = Boolean(route?.clientReferenceId && route?.pregnancyReferenceId && route?.laborEpisodeReferenceId);
  const items = routeScopedLaborItems(location, data?.items ?? []);
  const hasMore = !route && Boolean(data?.hasMore);

  const hidden = shouldHideBackToLaborButton({
    items,
    isLoading: !hasRouteTarget && isLoading,
    isError: !hasRouteTarget && isError,
    isFetching: !hasRouteTarget && isFetching,
    chartFocus,
  });

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  if (hidden) {
    return null;
  }

  const navigateToLabor = (item) => {
    const target = buildOpenLaborChartLocation(item);
    if (!target) return;
    setMenuOpen(false);
    navigate({ pathname: target.pathname, search: target.search }, { state: target.state });
  };

  const handlePrimaryAction = () => {
    if (items.length === 1) {
      navigateToLabor(items[0]);
      return;
    }
    setMenuOpen((open) => !open);
  };

  return (
    <div
      ref={containerRef}
      className="pointer-events-none fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-4 z-20 md:bottom-6 md:right-6"
    >
      <div className="pointer-events-auto flex flex-col items-end gap-2">
        {menuOpen && items.length > 1 && (
          <div
            id={menuId}
            role="menu"
            aria-label="Open labors"
            className="w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
          >
            <ul className="max-h-64 overflow-y-auto py-1">
              {items.map((item) => {
                const key = `${item.clientReferenceId}:${item.pregnancyReferenceId}:${item.laborEpisodeReferenceId}`;
                return (
                  <li key={key} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-start justify-between gap-2 px-3 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4f7a64]"
                      onClick={() => navigateToLabor(item)}
                    >
                      <span className="min-w-0 font-medium [overflow-wrap:anywhere]">{item.displayName}</span>
                      <span className="flex shrink-0 flex-wrap justify-end gap-1">
                        {item.isPushing ? <LaborStatusBadge label="Pushing" tone="accent" /> : null}
                        {item.hasLaborTransfer ? <LaborStatusBadge label="Transferring" /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {hasMore ? (
              <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                More open labors…
              </p>
            ) : null}
          </div>
        )}

        <button
          ref={triggerRef}
          type="button"
          aria-haspopup={items.length > 1 ? 'menu' : undefined}
          aria-expanded={items.length > 1 ? menuOpen : undefined}
          aria-controls={menuOpen ? menuId : undefined}
          onClick={handlePrimaryAction}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#3d6350] bg-[#4f7a64] px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-[#3d6350] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f7a64] focus-visible:ring-offset-2"
        >
          <FiChevronUp className="h-4 w-4 shrink-0" aria-hidden="true" />
          Back to labor
          {items.length > 1 ? (
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold" aria-label={`${items.length} open labors`}>
              {items.length}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
